// @mui
import { enUS, frFR, esES } from '@mui/material/locale';
// components
import { SettingsValueProps } from './components/settings/type';
// routes
import { PATH_MAIN } from './routes/paths';
// env
import { env } from './config/env';

// ENV
export const ENV = env.appEnv;
export const APP_URL = env.appUrl;

// API
// ----------------------------------------------------------------------
export const GQL_API = env.backend.gqlApi;
export const REST_API = env.backend.restApi;

// ROOT PATH AFTER LOGIN SUCCESSFUL
export const DEFAULT_PATH_AFTER_LOGIN = PATH_MAIN.general.dashboard; // as '/MAIN/app'

// LAYOUT
// ----------------------------------------------------------------------

// Environment Detection
export const IS_DEV = env.isDev;
export const IS_PROD = env.isProd;
export const IS_STAGING = env.isStaging;
export const TEST_MODE = false;

// Intercom
export const INTERCOM_ENABLED = env.intercom.enabled;

export const HEADER = {
    MOBILE_HEIGHT: 64,
    STATIC_DESKTOP_HEIGHT: 88,
    MAIN_DESKTOP_HEIGHT: 92,
    MAIN_DESKTOP_OFFSET_HEIGHT: 92 - 32,
};

export const NAVBAR = {
    BASE_WIDTH: 260,
    MAIN_WIDTH: 280,
    MAIN_COLLAPSE_WIDTH: 88,
    //
    MAIN_ITEM_ROOT_HEIGHT: 48,
    MAIN_ITEM_SUB_HEIGHT: 40,
    MAIN_ITEM_HORIZONTAL_HEIGHT: 32,
};

export const ICON = {
    NAVBAR_ITEM: 24,
    NAVBAR_ITEM_HORIZONTAL: 24,
};

// SETTINGS
// Please remove `localStorage` when you change settings.
// ----------------------------------------------------------------------

export const defaultSettings: SettingsValueProps = {
    themeMode: 'light',
    themeDirection: 'ltr',
    themeContrast: 'default',
    themeLayout: 'horizontal',
    themeColorPresets: 'default',
    themeStretch: false,
};

// MULTI LANGUAGES
// Please remove `localStorage` when you change settings.
// ----------------------------------------------------------------------

/**
 * Interface languages offered in the header switcher.
 *
 * Every entry must have a bundle in `src/locales/json` AND a `languages` row
 * flagged `isPlatformLocale` in the backend — `updateLanguage` rejects anything
 * else, so an entry without both shows the user an error instead of switching.
 *
 * `pt` and `ht` were listed here with neither a bundle nor a backend row, so
 * selecting them never worked: signed-in users got an error toast from
 * updateLanguage, and anonymous users fell through to the English bundle while
 * the switcher kept showing the PT/HT flag. Both have been removed.
 *
 * Portuguese and Haitian Creole remain available as *spoken* languages on
 * provider profiles, which is a separate list — see the `isSpokenOption` flag.
 */
export const allLangs = [
    {
        label: 'EN',
        value: 'en',
        systemValue: enUS,
        altCode: 'en-us',
        icon: '/assets/icons/flags/ic_flag_us.svg',
    },
    {
        label: 'ES',
        value: 'es',
        systemValue: esES,
        icon: '/assets/icons/flags/ic_flag_es.svg',
        altCode: 'es-es',
    },
    {
        label: 'FR',
        value: 'fr',
        systemValue: frFR,
        icon: '/assets/icons/flags/ic_flag_fr.svg',
        altCode: 'fr-fr',
    },
];

// navigator.language may return multiple formats that are valid in BCP47.
const possibleDefaultSystemLang = navigator.language.toLowerCase();

// Find best-matching language based on splitting criteria
// 1. Exact match with altCode or value (navigator.language returned exact match ('en-US', 'fr-FR', 'es-ES', etc.))
// 2. Partial match with value or starting altCode (navigator.language returned only language code ('en', 'fr', 'es', etc.))
// 3. Fallback to first language in the list (default to 'en')

const findMatchingLang = () => {
    console.log('navigatorlang', possibleDefaultSystemLang);
    const normalizedLang = possibleDefaultSystemLang;

    const exactMatch = allLangs.find(
        (lang) => lang.altCode === normalizedLang || lang.value === normalizedLang
    );
    if (exactMatch) return exactMatch;

    const langCode = normalizedLang.split('-')[0];
    const partialMatch = allLangs.find(
        (lang) => lang.value === langCode || lang.altCode.startsWith(langCode)
    );
    if (partialMatch) return partialMatch;

    return allLangs[0];
};

export const defaultLang = findMatchingLang();
